import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, CalendarHeart } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";
import { Wordmark } from "@/components/Wordmark";

export const Navbar = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, target) => {
    setOpen(false);
    scrollToSection(e, target);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-navy/[0.06] bg-cream/90 shadow-ambient backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10"
        aria-label="Primary"
      >
        <Wordmark />

        <ul className="hidden items-center gap-7 xl:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.testId}>
              <a
                href={link.target}
                onClick={(e) => go(e, link.target)}
                data-testid={link.testId}
                className="text-sm font-medium text-navy/80 transition-colors duration-200 hover:text-mauve-dark"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBook}
            data-testid="nav-cta-book-consultation"
            className="hidden items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-cream shadow-ambient transition-all duration-300 hover:-translate-y-0.5 hover:bg-mauve-dark hover:shadow-elevated sm:inline-flex"
          >
            <CalendarHeart className="h-4 w-4" aria-hidden="true" />
            Book a Consultation
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-navy/10 bg-white/70 text-navy backdrop-blur transition-colors hover:bg-white xl:hidden"
          >
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-navy/[0.06] bg-cream/95 backdrop-blur-xl xl:hidden"
            data-testid="nav-mobile-menu"
          >
            <ul className="space-y-1 px-6 py-6">
              {NAV_LINKS.map((link) => (
                <li key={link.testId}>
                  <a
                    href={link.target}
                    onClick={(e) => go(e, link.target)}
                    data-testid={`${link.testId}-mobile`}
                    className="block rounded-xl px-4 py-3 font-serif text-xl text-navy transition-colors hover:bg-mauve-light"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              <li className="pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onBook();
                  }}
                  data-testid="nav-cta-book-consultation-mobile"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 text-sm font-semibold text-cream transition-colors hover:bg-mauve-dark"
                >
                  <CalendarHeart className="h-4 w-4" aria-hidden="true" />
                  Book a Consultation
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
